import { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { PackagePlus } from 'lucide-react-native';

import { Button, Sheet, TextField } from '@/components/ui';
import { ingresarStock } from '@/services/firebase/ventas';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs, formatearFechaInput, parsearFechaDDMMYYYY, dateToDDMMYYYY } from '@/utils/format';
import type { ProductoVenta, InsumoInterno } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  producto: ProductoVenta | InsumoInterno | null;
  tipo: 'venta' | 'interno';
}

export function IngresarStockSheet({ visible, onClose, producto, tipo }: Props) {
  const perfil = useAuth((s) => s.perfil);
  const [cantidad, setCantidad] = useState('');
  const [costoTotal, setCostoTotal] = useState('');
  const [registrarEgreso, setRegistrarEgreso] = useState(true);
  const [vencimiento, setVencimiento] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !producto) return;
    setCantidad('');
    setCostoTotal('');
    setRegistrarEgreso(true);
    setVencimiento(producto.vencimiento?.toDate ? dateToDDMMYYYY(producto.vencimiento.toDate()) : '');
    setNotas('');
    setError(null);
    setSaving(false);
  }, [visible, producto]);

  if (!producto) return null;

  const cantN = Number(cantidad);
  const costoN = Number(costoTotal.replace(',', '.'));
  const stockNuevo = (producto.stock ?? 0) + (Number.isFinite(cantN) ? cantN : 0);
  const costoUnit = Number.isFinite(cantN) && cantN > 0 && Number.isFinite(costoN) && costoN > 0
    ? costoN / cantN
    : null;

  const onSubmit = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    if (!Number.isFinite(cantN) || cantN <= 0) return setError('Cantidad inválida.');

    let venc: Date | null = null;
    if (vencimiento.trim()) {
      const d = parsearFechaDDMMYYYY(vencimiento);
      if (!d) return setError('Fecha de vencimiento inválida. Usá DD/MM/AAAA.');
      venc = d;
    }

    setSaving(true);
    try {
      await ingresarStock({
        productoId: producto.id,
        coleccion: tipo === 'venta' ? 'productos_venta' : 'insumos_internos',
        cantidad: cantN,
        costoTotal: registrarEgreso && Number.isFinite(costoN) && costoN > 0 ? costoN : undefined,
        proveedorId: producto.proveedorId,
        proveedorNombre: producto.proveedorNombre,
        vencimiento: venc,
        notas: notas.trim() || undefined,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
        registrarEgreso,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo ingresar stock.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title="Ingresar stock"
      subtitle={`${producto.nombre} · ${producto.presentacion}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label="Confirmar ingreso"
            iconLeft={<PackagePlus size={16} color={colors.white} />}
            onPress={onSubmit}
            loading={saving}
          />
        </>
      }
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, padding: 14, borderRadius: radius['2xl'], backgroundColor: colors.crema[50] }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Stock actual
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 26, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            {producto.stock}
          </Text>
        </View>
        <Text style={{ fontSize: 22, color: colors.ink[400] }}>→</Text>
        <View style={{ flex: 1, alignItems: 'flex-end' }}>
          <Text style={{ fontSize: 11, color: colors.success[600], fontWeight: typography.weights.bold, letterSpacing: 0.8, textTransform: 'uppercase' }}>
            Stock nuevo
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 26, fontWeight: typography.weights.bold, color: colors.success[600] }}>
            {Number.isFinite(stockNuevo) ? stockNuevo : '—'}
          </Text>
        </View>
      </View>

      <TextField label="Cantidad a ingresar" placeholder="Ej: 10" value={cantidad} onChangeText={setCantidad} keyboardType="numeric" />

      <View style={{ gap: 8 }}>
        <Pressable
          onPress={() => setRegistrarEgreso(!registrarEgreso)}
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              borderWidth: 2,
              borderColor: registrarEgreso ? colors.magenta[700] : colors.ink[300],
              backgroundColor: registrarEgreso ? colors.magenta[700] : colors.white,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {registrarEgreso ? <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 14 }}>✓</Text> : null}
          </View>
          <Text style={{ fontSize: 14, color: colors.ink[800], fontWeight: typography.weights.semibold }}>
            Registrar egreso contable por la compra
          </Text>
        </Pressable>
        {registrarEgreso ? (
          <View style={{ paddingLeft: 32, gap: 6 }}>
            <TextField label="Costo total de la compra (Bs)" placeholder="0.00" value={costoTotal} onChangeText={setCostoTotal} keyboardType="decimal-pad" />
            {costoUnit ? (
              <Text style={{ fontSize: 12, color: colors.ink[500] }}>
                Costo unitario: {formatBs(costoUnit)}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>

      <TextField
        label="Vencimiento (opcional)"
        placeholder="DD/MM/AAAA"
        value={vencimiento}
        onChangeText={(t) => setVencimiento(formatearFechaInput(t))}
        keyboardType="numeric"
        hint="Si este lote vence en otra fecha, ingresála."
      />
      <TextField label="Notas (opcional)" placeholder="Lote, factura, observaciones…" value={notas} onChangeText={setNotas} multiline />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
